import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { User, Edit, X, Check, UserCircle, AlertCircle, Moon, Bell, Mail, LogOut, HelpCircle, Lightbulb } from 'lucide-react';
import { useAuth, API, getAuthHeaders } from '@/App';
import { toast } from 'sonner';

// Simple color avatar options for anonymous identity
const AVATAR_OPTIONS = [
  { id: 'blue', bg: '#3b82f6' },
  { id: 'indigo', bg: '#6366f1' },
  { id: 'purple', bg: '#8b5cf6' },
  { id: 'pink', bg: '#ec4899' },
  { id: 'red', bg: '#ef4444' },
  { id: 'orange', bg: '#f97316' },
  { id: 'amber', bg: '#f59e0b' },
  { id: 'yellow', bg: '#eab308' },
  { id: 'lime', bg: '#84cc16' },
  { id: 'green', bg: '#22c55e' },
  { id: 'emerald', bg: '#10b981' },
  { id: 'teal', bg: '#14b8a6' },
  { id: 'cyan', bg: '#06b6d4' },
  { id: 'sky', bg: '#0ea5e9' },
  { id: 'slate', bg: '#64748b' },
];

// 80 emoji avatar options for profile picture (real identity)
const EMOJI_AVATARS = [
  // Faces
  '😀', '😎', '🥳', '😊', '🤓', '😇', '🤩', '😏', '🥰', '😍',
  '🤗', '🙂', '😌', '🤭', '😋', '🤔', '😴', '🤯', '🥶', '🤠',
  '🥸', '😤', '🧐', '🤨', '😈', '👹', '🤡', '💀', '☠️', '👾',
  // Characters
  '👻', '🤖', '👽', '🎃', '👸', '🤴', '🧙', '🧚', '🧛', '🦸',
  '🥷', '🧑‍🚀', '👨‍🎤', '👩‍🎨', '🧑‍💻', '👨‍🔬', '👩‍🏫', '🧑‍🍳', '🧑‍✈️', '🧑‍🎓',
  // Animals
  '🐶', '🦊', '🐱', '🐯', '🦁', '🐻', '🐼', '🐨', '🐰', '🦄',
  '🐸', '🐵', '🦋', '🐝', '🦅', '🦉', '🐺', '🦈', '🐬', '🐙',
  '🦎', '🐢', '🦖', '🦕', '🐲', '🐉', '🦩', '🦚', '🦜', '🐧',
  // Nature & Objects
  '🌸', '🌺', '🌻', '🍀', '🔥', '⭐', '🌙', '☀️', '🌈', '💎',
  '🎯', '🎨', '🎭', '🎪', '🎸', '🎮', '🎲', '🧩', '🏆', '🥇',
  '🚀', '🛸', '⚡', '💫', '🌊', '🗻', '🏝️', '🎄', '🍕', '🍩'
];

function AvatarPreview({ avatar, name, size = 'md' }) {
  const sizeClass = size === 'lg' ? 'w-20 h-20 text-3xl' : size === 'md' ? 'w-10 h-10 text-base' : 'w-8 h-8 text-sm';
  const avatarData = AVATAR_OPTIONS.find(a => a.id === avatar);
  
  if (!avatar || !avatarData) {
    return (
      <div className={`${sizeClass} rounded-full bg-gray-200 flex items-center justify-center shrink-0`}>
        <User className={size === 'lg' ? 'w-8 h-8' : 'w-4 h-4'} style={{ color: '#9ca3af' }} />
      </div>
    );
  }

  return (
    <div 
      className={`${sizeClass} rounded-full flex items-center justify-center font-bold text-white shrink-0`}
      style={{ background: avatarData.bg }}
    >
      <span>{name?.charAt(0)?.toUpperCase() || 'A'}</span>
    </div>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, checkAuth, logout } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [anonymousName, setAnonymousName] = useState(user?.anonymous_name || '');
  const [selectedAvatar, setSelectedAvatar] = useState(user?.avatar || '');
  const [selectedProfileAvatar, setSelectedProfileAvatar] = useState(user?.profile_avatar || '');
  const [loading, setLoading] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showProfileAvatarPicker, setShowProfileAvatarPicker] = useState(false);
  
  // Settings state - Initialize from user profile if available, fallback to localStorage
  const [darkMode, setDarkMode] = useState(() => {
    // First check user profile, then localStorage
    if (user?.dark_mode !== undefined) {
      return user.dark_mode;
    }
    return localStorage.getItem('darkMode') === 'true';
  });
  const [inAppNotifications, setInAppNotifications] = useState(user?.in_app_notifications !== false);
  const [emailNotifications, setEmailNotifications] = useState(user?.email_notifications !== false);
  const [savingNotifications, setSavingNotifications] = useState(false);

  // All useEffect hooks must be before any conditional returns
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setAnonymousName(user.anonymous_name || '');
      setSelectedAvatar(user.avatar || '');
      setSelectedProfileAvatar(user.profile_avatar || '');
      setInAppNotifications(user.in_app_notifications !== false);
      setEmailNotifications(user.email_notifications !== false);
      // Sync dark mode from user profile
      if (user.dark_mode !== undefined) {
        setDarkMode(user.dark_mode);
        localStorage.setItem('darkMode', user.dark_mode.toString());
        if (user.dark_mode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    }
  }, [user]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  // Save dark mode to backend
  const saveDarkModePreference = async (isDark) => {
    try {
      await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ dark_mode: isDark })
      });
    } catch (error) {
      console.error('Failed to save dark mode preference:', error);
    }
  };

  // Save notification preferences to backend
  const saveNotificationPreferences = async (inApp, email) => {
    setSavingNotifications(true);
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          in_app_notifications: inApp,
          email_notifications: email
        })
      });

      if (response.ok) {
        await checkAuth();
        toast.success('Notification preferences saved');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleInAppToggle = (checked) => {
    setInAppNotifications(checked);
    saveNotificationPreferences(checked, emailNotifications);
  };

  const handleEmailToggle = (checked) => {
    setEmailNotifications(checked);
    saveNotificationPreferences(inAppNotifications, checked);
  };

  // If user is not logged in, show sign in prompt (after all hooks)
  if (!user) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 rounded-xl p-8">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Sign in to view your profile</h2>
          <p className="text-gray-500 mb-6">Access your profile settings and preferences</p>
          <Button onClick={() => navigate('/login?mode=signup')} className="bg-indigo-600 hover:bg-indigo-700">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    setLoading(true);
    try {
      const updateData = { 
        name, 
        avatar: selectedAvatar,
        anonymous_name: anonymousName,
        profile_avatar: selectedProfileAvatar
      };

      const response = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await checkAuth();
        setEditing(false);
        setShowAvatarPicker(false);
        setShowProfileAvatarPicker(false);
        toast.success('Profile updated!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setName(user?.name || '');
    setAnonymousName(user?.anonymous_name || '');
    setSelectedAvatar(user?.avatar || '');
    setSelectedProfileAvatar(user?.profile_avatar || '');
    setEditing(false);
    setShowAvatarPicker(false);
    setShowProfileAvatarPicker(false);
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/home');
  };

  const isEmojiAvatar = selectedProfileAvatar && EMOJI_AVATARS.includes(selectedProfileAvatar);
  const currentIsEmoji = user?.profile_avatar && EMOJI_AVATARS.includes(user.profile_avatar);

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-24 md:pb-8">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'DM Sans' }}>
          Profile & Settings
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">Manage your account</p>
      </div>

      {/* Profile Card */}
      <div className="bg-gray-50 dark:bg-[#141414] rounded-xl border border-gray-200/50 dark:border-[#2a2a2a] p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">Your Profile</h2>
          {!editing ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="h-8 text-xs dark:border-[#3a3a3a] dark:text-gray-300 dark:hover:bg-[#1a1a1a]">
              <Edit className="w-3 h-3 mr-1" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel} className="h-8 text-xs dark:border-[#3a3a3a] dark:text-gray-300 dark:hover:bg-[#1a1a1a]">
                <X className="w-3 h-3 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={loading} className="h-8 text-xs bg-purple-600 hover:bg-purple-700 text-white">
                <Check className="w-3 h-3 mr-1" /> Save
              </Button>
            </div>
          )}
        </div>

        {/* Avatar Display */}
        <div className="flex items-center gap-4 mb-4">
          <Avatar className="w-16 h-16 border-2 border-white dark:border-purple-500/30 shadow-lg shrink-0">
            <AvatarFallback 
              className={editing ? (isEmojiAvatar ? "bg-gray-100 dark:bg-[#1a1a1a] text-3xl" : "bg-purple-500 text-white text-xl font-bold") : (currentIsEmoji ? "bg-gray-100 dark:bg-[#1a1a1a] text-3xl" : "bg-purple-500 text-white text-xl font-bold")}
            >
              {editing ? (isEmojiAvatar ? selectedProfileAvatar : (name?.charAt(0) || 'U')) : (currentIsEmoji ? user?.profile_avatar : (user?.name?.charAt(0) || 'U'))}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 dark:text-white truncate">{user?.name}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
          </div>
        </div>

        {editing ? (
          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label className="text-xs font-medium text-gray-700">Full Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 h-9 text-sm"
              />
            </div>

            {/* Profile Emoji Avatar */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-gray-700">Profile Emoji</Label>
                <button
                  onClick={() => setShowProfileAvatarPicker(!showProfileAvatarPicker)}
                  className="text-xs text-indigo-600 hover:text-indigo-700"
                >
                  {showProfileAvatarPicker ? 'Hide' : 'Choose'}
                </button>
              </div>
              {showProfileAvatarPicker && (
                <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200">
                  <div className="grid grid-cols-8 gap-2">
                    <button
                      onClick={() => setSelectedProfileAvatar('')}
                      className={`w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold ${!selectedProfileAvatar ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}`}
                    >
                      {name?.charAt(0) || 'U'}
                    </button>
                    {EMOJI_AVATARS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => setSelectedProfileAvatar(emoji)}
                        className={`w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-lg hover:bg-gray-100 ${selectedProfileAvatar === emoji ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Anonymous Identity Section */}
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <UserCircle className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-semibold text-purple-700">Anonymous Identity</span>
              </div>
              
              <div className="mb-3">
                <Label className="text-xs font-medium text-gray-700">Anonymous Username</Label>
                <Input
                  value={anonymousName}
                  onChange={(e) => setAnonymousName(e.target.value)}
                  placeholder="e.g., Curious Fox"
                  className="mt-1 h-9 text-sm"
                />
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Keep it university-friendly
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-gray-700">Anonymous Color</Label>
                  <button
                    onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                    className="text-xs text-indigo-600 hover:text-indigo-700"
                  >
                    {showAvatarPicker ? 'Hide' : 'Choose'}
                  </button>
                </div>
                {showAvatarPicker && (
                  <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200">
                    <div className="grid grid-cols-8 gap-2">
                      {AVATAR_OPTIONS.map((avatar) => (
                        <button
                          key={avatar.id}
                          onClick={() => setSelectedAvatar(avatar.id)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${selectedAvatar === avatar.id ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}`}
                          style={{ background: avatar.bg }}
                        >
                          {anonymousName?.charAt(0) || 'A'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {selectedAvatar && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Preview:</span>
                    <AvatarPreview avatar={selectedAvatar} name={anonymousName} size="sm" />
                    <span className="text-xs text-purple-700 font-medium">{anonymousName || 'Anonymous'}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-xs text-gray-500">Name</span>
              <span className="text-sm font-medium text-gray-900">{user?.name}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-xs text-gray-500">Email</span>
              <span className="text-sm text-gray-700">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-xs text-gray-500">Anonymous Name</span>
              <div className="flex items-center gap-2">
                {user?.avatar && <AvatarPreview avatar={user.avatar} name={user.anonymous_name} size="sm" />}
                <span className="text-sm text-purple-700 font-medium">{user?.anonymous_name || 'Not set'}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Settings Section */}
      <div className="bg-gray-50 dark:bg-[#141414] rounded-xl border border-gray-200/50 dark:border-[#2a2a2a] p-4">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-4">Settings</h2>
        
        <div className="space-y-4">
          {/* Dark Mode */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center">
                <Moon className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Dark Mode</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Switch to dark theme</p>
              </div>
            </div>
            <Switch checked={darkMode} onCheckedChange={(checked) => {
              setDarkMode(checked);
              saveDarkModePreference(checked);
            }} />
          </div>

          {/* Notifications Header */}
          <div className="pt-3 border-t border-gray-200 dark:border-[#2a2a2a]">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Notifications</p>
          </div>

          {/* In-App Notifications */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-purple-600 rounded-lg flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">In-App Notifications</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Bell icon alerts for messages & replies</p>
              </div>
            </div>
            <Switch 
              checked={inAppNotifications} 
              onCheckedChange={handleInAppToggle}
              disabled={savingNotifications}
            />
          </div>

          {/* Email Notifications */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Receive emails for DMs, replies & posts</p>
              </div>
            </div>
            <Switch 
              checked={emailNotifications} 
              onCheckedChange={handleEmailToggle}
              disabled={savingNotifications}
            />
          </div>
        </div>
      </div>

      {/* Help & Support */}
      <div className="bg-gray-50 dark:bg-[#141414] rounded-xl border border-gray-200/50 dark:border-[#2a2a2a] p-4">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3">Help & Support</h2>
        <div className="space-y-2">
          <button
            onClick={() => navigate('/help')}
            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors"
          >
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-500/20 rounded-lg flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900 dark:text-white">Help & Guide</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Learn how to use UniConnect</p>
            </div>
          </button>
          <button
            onClick={() => navigate('/suggestions')}
            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors"
          >
            <div className="w-8 h-8 bg-amber-100 dark:bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900 dark:text-white">Suggestions</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Request features or courses</p>
            </div>
          </button>
        </div>
      </div>

      {/* Logout */}
      <Button
        onClick={handleLogout}
        className="w-full bg-red-500 hover:bg-red-600 text-white rounded-xl h-11 font-semibold"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Logout
      </Button>
    </div>
  );
}

export { AvatarPreview, AVATAR_OPTIONS, EMOJI_AVATARS };
